import * as React from 'react';
import { Messages } from '../../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../../commonInfra/components/FormikCheckbox';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { ExtensionsSettingsSubspaceState } from '../../../../../app/spaces/SiteSettingsSpace/ExtensionsSettingsSubspace';
import {
  ButtonType,
  ButtonWidget,
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  TextFieldWidget,
  TextLinkWidget
} from '@tableau/widgets';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { useSelector } from '../../../utils';
import { SessionServerSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { Sites } from '~src/ts/siteManagement/services/resources/Sites';
import { Formik, FormikProps, useFormikContext } from 'formik';
import { ExtensionSafeListEntry } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { useSettingsCoordinator } from '~src/settings/components/settings-coordinator/SettingsCoordinatorHook';
import { wideField } from '../../../styles/SiteNameAndIdStyles';
import { ExtensionsSettingsSubFormikProps } from '../ExtensionsSettingsCoordinator';
import { DataGrid } from '@tableau/widgets-datagrid';
import { QuickTooltip } from './QuickTooltip';
import {
  addButtonInfoStyle,
  addButtonStyle,
  buttonStyle,
  dataGridContainerStyle,
  dataGridRowHeight,
  dataGridSafeContainerStyle,
  dropDownStyle,
  sectionHeader,
  textFieldStyle
} from './ExtensionsSectionStyle';
import {
  ADD_SAFE_URL_BUTTON_TEST_ID,
  ADD_SAFE_URL_TOOLTIP_TEST_ID,
  DELETE_SAFE_URL_BUTTON_PREFIX_TEST_ID,
  EXTENSIONS_DISABLED_WARNING_TEST_ID,
  SITE_EXTENSIONS_ENABLED_TEST_ID,
  SITE_EXTENSIONS_LEARN_MORE_LINK_TEST_ID,
  FULL_DATA_PREFIX_TEST_ID,
  FULL_DATA_TOOLTIP_TEST_ID,
  SAFELIST_EMPTY_TEST_ID,
  SAFE_URL_INPUT_PREFIX_TEST_ID,
  SANDBOXED_EXTENSIONS_ENABLED_TEST_ID,
  USER_PROMPT_PREFIX_TEST_ID,
  USER_PROMPT_TOOLTIP_TEST_ID
} from './ExtensionsTestIds';

export const EXTENSIONS_ENABLED_FIELD = 'settings.dashExtSiteSettings.extensionsEnabled';
export const SANDBOXED_EXTENSIONS_ENABLED_FIELD = 'settings.dashExtSiteSettings.allowSandboxed';

const DEFAULT_URL = 'https://';
const DEFAULT_FULL_DATA_ACCESS = false;
const DEFAULT_USER_PROMPT = true;

interface DropDownOption {
  label: string;
  value: string;
}


type ExtensionsSiteFormField = { safelist: ExtensionSafeListEntry[] }
type ExtensionsSiteFormikProps = FormikProps<ExtensionsSiteFormField>;

export function ExtensionsSiteSection(props: ExtensionsSettingsSubFormikProps) {
  const [safelist, setSafelist] = React.useState<ExtensionSafeListEntry[]>([]);
  const initialValues = React.useMemo(() => {
    return { safelist };
  }, [safelist]);

  function mapSafeListEntries() {
    const safelist = props.settings.dashExtSiteSettings.safeList.map((v: ExtensionSafeListEntry) => {
      const safelistEntry: ExtensionSafeListEntry = {
        url: v.url,
        fullDataAllowed: v.fullDataAllowed,
        promptNeeded: v.promptNeeded
      };
      return safelistEntry;
    });
    setSafelist(safelist);
  }

  React.useEffect(() => mapSafeListEntries(), []);

  const handleSaveAction = React.useCallback(async (values: ExtensionsSiteFormField, formik: ExtensionsSiteFormikProps) => {
    const safelist: ExtensionSafeListEntry[] = values.safelist.map((v: ExtensionSafeListEntry) => {
      const safelistEntry: ExtensionSafeListEntry = {
        url: v.url,
        fullDataAllowed: v.fullDataAllowed,
        promptNeeded: v.promptNeeded
      };
      return safelistEntry;
    });
    props.settings.dashExtSiteSettings.safeList = safelist;
    mapSafeListEntries();
  }, [initialValues.safelist]);

  return (
    <SettingsSection title={props.settings.isMultiSite ? Messages.extensionsSettings_header() : ''}
                     description={props.settings.isMultiSite ? renderDescription()  : ''}>

      {!props.settings.dashExtServerSettings.extensionsGloballyEnabled &&
        <div data-tb-test-id={EXTENSIONS_DISABLED_WARNING_TEST_ID} className={'tb-warning-text tb-settings-group-name'}>
          {Messages.extensionsSiteSettings_disabledOnServerMessage()}
        </div>
      }

      {props.settings.isMultiSite &&
        <FormikCheckbox name={EXTENSIONS_ENABLED_FIELD}
                        label={Messages.extensionsSiteSettings_allowForSite()}
                        testId={SITE_EXTENSIONS_ENABLED_TEST_ID}>
        </FormikCheckbox>
      }

      <FormikCheckbox name={SANDBOXED_EXTENSIONS_ENABLED_FIELD}
                      label={useSelectAllowSandboxByServerEnvironment()}
                      testId={SANDBOXED_EXTENSIONS_ENABLED_TEST_ID}>
      </FormikCheckbox>

      <div className={sectionHeader}>{Messages.extensionsSiteSettings_whiteListHeader()}</div>

      <Formik initialValues={initialValues}
              enableReinitialize={true}
              onSubmit={(values: ExtensionsSiteFormField, formik: ExtensionsSiteFormikProps) => handleSaveAction(values, formik)}>
        <SafeList />
      </Formik>
    </SettingsSection>
  );
}

function renderDescription() {
  return (
    <div>
      {useSelectDescriptionByServerEnvironment()}
      <TextLinkWidget testId={SITE_EXTENSIONS_LEARN_MORE_LINK_TEST_ID}
                      target={'_blank'}
                      className={learnMoreStyle}
                      href={HelpService.getServerHelpUrlByCategory('extensions')}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </div>
  );
}

function handleUrlChange(idx: number, formik: ExtensionsSiteFormikProps, event: React.ChangeEvent<HTMLInputElement>) {
  const clients = [...formik.values.safelist];
  clients[idx] = {...clients[idx], url: event.target.value};
  formik.setFieldValue('safelist', clients, false);
}

function handleFullDataOptionChange(idx: number, formik: ExtensionsSiteFormikProps, value: string) {
  const temp = [...formik.values.safelist];
  temp[idx] = {...temp[idx], fullDataAllowed: value === 'true'};
  formik.setFieldValue('safelist', temp, false);
}

function handlePromptNeededOptionChange(idx: number, formik: ExtensionsSiteFormikProps, value: string) {
  const temp = [...formik.values.safelist];
  temp[idx] = {...temp[idx], promptNeeded: value === 'true'};
  formik.setFieldValue('safelist', temp, false);
}

export function handleDeleteClient(idx: number, formik: ExtensionsSiteFormikProps) {
  if (idx > -1) {
    const clients = [...formik.values.safelist];
    clients.splice(idx, 1);
    formik.setFieldValue('safelist', clients, false);
  }
}

export function handleAddClient(formik: ExtensionsSiteFormikProps) {
  const clients = [...formik.values.safelist];
  clients.unshift({
    url: DEFAULT_URL,
    fullDataAllowed: DEFAULT_FULL_DATA_ACCESS,
    promptNeeded: DEFAULT_USER_PROMPT
  });
  formik.setFieldValue('safelist', clients, false);
}

function useSelectDescriptionByServerEnvironment() {
  return useSelector<boolean>(SessionServerSelectors.isOnlineEnvironment)
    ? Messages.extensionsSiteSettings_descriptionWhat_online()
    : Messages.extensionsSiteSettings_descriptionWhat_server();
}

function useSelectAllowSandboxByServerEnvironment() {
  return useSelector<boolean>(SessionServerSelectors.isOnlineEnvironment)
    ? Messages.extensionsSettings_allowSandboxedExtensions_online()
    : Messages.extensionsSettings_allowSandboxedExtensions_server();
}

function SafeList() {
  const FullDataOptions: DropDownOption[] = [
    {label: Messages.extensionsSiteSettings_whitelist_FullDataAccess_Allow(), value: 'true'},
    {label: Messages.extensionsSiteSettings_whitelist_FullDataAccess_Deny(), value: 'false'}
  ];

  const PromptNeededOptions: DropDownOption[] = [
    {label: Messages.extensionsSiteSettings_whitelist_UserPrompts_Show(), value: 'true'},
    {label: Messages.extensionsSiteSettings_whitelist_UserPrompts_Hide(), value: 'false'}
  ];

  const fullDataList: ListItem[] = FullDataOptions.map((option: DropDownOption) => {
    const listItem: ListItem = {
      content: option.label,
      value: option.value
    };
    return listItem;
  });

  const promptNeededList: ListItem[] = PromptNeededOptions.map((option: DropDownOption) => {
    const listItem: ListItem = {
      content: option.label,
      value: option.value
    };
    return listItem;
  });

  useSettingsCoordinator();
  const formik = useFormikContext<ExtensionsSiteFormField>();

  enum SafeListDataKeys {
    SafeUrl = 'safeUrl',
    FullData = 'fullData',
    UserPrompt = 'userPrompt',
  }

  const columns: DataGrid.Column[] = [];
  columns.push({
    name: SafeListDataKeys.SafeUrl,
    dataKey: SafeListDataKeys.SafeUrl,
    headerText: Messages.extensionsSettings_list_URLHeader(),
    headerFormatter: (_) => {
      return <span className={addButtonInfoStyle}>
        {Messages.extensionsSettings_list_URLHeader()}
        <ButtonWidget buttonType={ButtonType.Outline}
          testId={ADD_SAFE_URL_BUTTON_TEST_ID}
          className={`${buttonStyle} ${addButtonStyle}`}
          onClick={() => handleAddClient(formik) }
          disabled={false}
          type='button'>{Messages.extensionsSettings_addUrlButton()}
        </ButtonWidget>
        <QuickTooltip
          testId={ADD_SAFE_URL_TOOLTIP_TEST_ID}
          content={Messages.extensionsSiteSettings_whitelist_URLHeaderInfo()} />
      </span>;
    },
    growToFit: true,
    resizable: false,
    sortable: false,
    cellFormatter: (props) => {
      return <TextFieldWidget testId={`${SAFE_URL_INPUT_PREFIX_TEST_ID}${props.rowIdx}`}
        className={`${wideField} ${textFieldStyle}`}
        value={props.row.data[SafeListDataKeys.SafeUrl]}
        required={true}
        label={null}
        onChange={(event) => handleUrlChange(props.rowIdx, formik, event)}
      />;
    },
  });
  columns.push({
    name: SafeListDataKeys.FullData,
    dataKey: SafeListDataKeys.FullData,
    headerText: '',
    headerFormatter: (_) => {
      return <span>
        {Messages.extensionsSiteSettings_whitelist_FullDataAccessHeader()}
        <QuickTooltip
          testId={FULL_DATA_TOOLTIP_TEST_ID}
          content={Messages.extensionsSiteSettings_whitelist_FullDataInfo()} />
      </span>;
    },
    resizable: false,
    preferredWidth: 125,
    sortable: false,
    cellFormatter: (props) => {
      return <SingleSelectDropdown
        className={dropDownStyle}
        testId={`${FULL_DATA_PREFIX_TEST_ID}${props.rowIdx}`}
        list={<List items={fullDataList} />}
        value={props.row.data[SafeListDataKeys.FullData] ? 'true' : 'false'}
        type={DropdownButtonType.Outline}
        onChange={(value) => handleFullDataOptionChange(props.rowIdx, formik, value)}
        label={null}
      />;
    },
  });
  columns.push({
    name: SafeListDataKeys.UserPrompt,
    dataKey: SafeListDataKeys.UserPrompt,
    headerText: '',
    headerFormatter: (_) => {
      return <span>
        {Messages.extensionsSiteSettings_whitelist_UserPromptsHeader()}
        <QuickTooltip
          testId={USER_PROMPT_TOOLTIP_TEST_ID}
          content={Messages.extensionsSiteSettings_whitelist_UserPromptsInfo()} />
      </span>;
    },
    resizable: false,
    preferredWidth: 110,
    sortable: false,
    cellFormatter: (props) => {
      return <SingleSelectDropdown
        className={dropDownStyle}
        testId={`${USER_PROMPT_PREFIX_TEST_ID}${props.rowIdx}`}
        list={<List items={promptNeededList} />}
        value={props.row.data[SafeListDataKeys.UserPrompt] ? 'true' : 'false'}
        type={DropdownButtonType.Outline}
        onChange={(value) => handlePromptNeededOptionChange(props.rowIdx, formik, value)}
        label={null}
      />;
    },
  });
  columns.push({
    name: '',
    dataKey: '',
    headerText: '',
    resizable: false,
    sortable: false,
    cellFormatter: (props) => {
      return <ButtonWidget buttonType={ButtonType.LowEmphasis}
        testId={`${DELETE_SAFE_URL_BUTTON_PREFIX_TEST_ID}${props.rowIdx}`}
        className={buttonStyle}
        onClick={() => handleDeleteClient(props.rowIdx, formik)}
        disabled={false}
        type='button'>{Messages.deleteMessage()}
      </ButtonWidget>;
    },
  });

  let rows: DataGrid.Row[] = [];
  let rowIndex = 0;
  formik.values.safelist.forEach(rowData => {
    let rowItemData: { [key: string]: {} } = {};
    rowItemData[SafeListDataKeys.SafeUrl] = rowData.url;
    rowItemData[SafeListDataKeys.FullData] = rowData.fullDataAllowed;
    rowItemData[SafeListDataKeys.UserPrompt] = rowData.promptNeeded;
    rows.push({
      key: rowIndex.toString(),
      data: rowItemData
    });
    rowIndex++;
  });

  return <div>
    <div className={`tb-spaces-list-view ${dataGridContainerStyle} ${dataGridSafeContainerStyle}`}>
      <DataGrid
        config= {{
          rowOuterHeight: dataGridRowHeight,
          rememberLastScrollPos: true
        }}
        emptyChild={
          <div data-tb-test-id={SAFELIST_EMPTY_TEST_ID} className="tb-data-grid-cell-text tb-data-grid-bridge-center">
            {Messages.extensionsSiteSettings_whitelist_emptyMessage()}
          </div>
        }
        cols={columns}
        rowStore={{
          getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
            rowHandler(rows);
          }
        }}
        showVerticalGridLines={false}
        showHorizontalGridLines={false}
        showRowBanding={true}
      />
    </div>
  </div>;
}

export const ExtensionsSiteSectionSpecs = componentRegistryEntry({
  name: 'ExtensionsSiteSection',
  component: ExtensionsSiteSection,
  onSubmit: async (settings: ExtensionsSettingsSubspaceState) => {
    await Promise.all([Sites.instance.updateCurrentExtensionsSiteSettingsForSiteAdmin(settings.dashExtSiteSettings)]);
  },
});
